import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseBankExport, type ParseResult } from '../services/xlsxParser';
import {
  classify,
  isCreditCardPayment,
} from '../services/classificationService';
import { excludeMSIPrincipalTransactions } from '../services/msiFeeDetectionService';
import { useAppStore } from '../store/appStore';
import { formatFileSize, formatDateRange } from '../utils/format';
import type { Transaction } from '../types';

type ImportStatus = 'idle' | 'parsing' | 'success' | 'error';

interface ImportSummary {
  transactionsImported: number;
  duplicatesSkipped: number;
  cardsDetected: number;
  dateRange: { start: string; end: string } | null;
  errors: string[];
}

export function ImportPage() {
  const navigate = useNavigate();
  const { transactions: existingTransactions, addTransactions } = useAppStore();

  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const processFile = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      setStatus('parsing');
      setError(null);
      setSummary(null);

      try {
        const result: ParseResult = await parseBankExport(file);

        if (result.errors.length > 0 && result.transactions.length === 0) {
          setStatus('error');
          setError(result.errors.join('\n'));
          return;
        }

        const importBatchId = crypto.randomUUID();
        const importedAt = new Date().toISOString();

        const existingKeys = new Set(
          existingTransactions.map(
            (t) => `${t.date}-${t.description}-${t.amount}-${t.cardIdentifier}`
          )
        );

        const newTransactions: Transaction[] = [];
        let duplicatesSkipped = 0;

        for (const tx of result.transactions) {
          const key = `${tx.date}-${tx.description}-${tx.amount}-${tx.cardIdentifier}`;
          if (existingKeys.has(key)) {
            duplicatesSkipped++;
            continue;
          }
          existingKeys.add(key);

          const classification = classify(tx.description);
          const excludedFromBudget =
            isCreditCardPayment(tx.description) || tx.isInstallment;
          newTransactions.push({
            ...tx,
            id: crypto.randomUUID(),
            categoryId: classification.categoryId,
            importedAt,
            importBatchId,
            excludedFromBudget,
          });
        }

        // Apply MSI principal transaction detection and exclusion
        const processedTransactions =
          excludeMSIPrincipalTransactions(newTransactions);

        if (processedTransactions.length > 0) {
          addTransactions(processedTransactions);
        }

        const dates = processedTransactions.map((t) => t.date).sort();
        const dateRange =
          dates.length > 0
            ? { start: dates[0], end: dates[dates.length - 1] }
            : null;

        setSummary({
          transactionsImported: newTransactions.length,
          duplicatesSkipped,
          cardsDetected: result.cardInfo.length,
          dateRange,
          errors: result.errors,
        });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setError(
          err instanceof Error
            ? err.message
            : 'Error desconocido al procesar el archivo'
        );
      }
    },
    [existingTransactions, addTransactions]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (status !== 'parsing') {
        setIsDragging(true);
      }
    },
    [status]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (status === 'parsing') return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (
          file.name.endsWith('.xlsx') ||
          file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          processFile(file);
        } else {
          setError('Solo se aceptan archivos .xlsx');
          setStatus('error');
        }
      }
    },
    [status, processFile]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile]
  );

  const handleReset = useCallback(() => {
    setSelectedFile(null);
    setStatus('idle');
    setSummary(null);
    setError(null);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-brand-primary mb-2 text-center">
        Importar Transacciones
      </h1>
      <p className="text-mid-gray text-center max-w-md mb-8">
        Importa tu archivo de movimientos bancarios para comenzar a organizar
        tus finanzas.
      </p>

      {status === 'idle' && (
        <div className="w-full max-w-md">
          <div
            role="button"
            tabIndex={0}
            aria-label="Zona de carga de archivos"
            onClick={() => document.getElementById('file-input')?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                document.getElementById('file-input')?.click();
              }
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors duration-200
              ${
                isDragging
                  ? 'border-brand-primary bg-brand-primary/10'
                  : 'border-light-gray hover:border-brand-secondary hover:bg-brand-secondary/5'
              }
            `}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              className="hidden"
              data-testid="file-input"
            />

            <div className="flex flex-col items-center gap-3">
              <svg
                className={`w-12 h-12 ${isDragging ? 'text-brand-primary' : 'text-mid-gray'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>

              <div>
                <p className="text-near-black font-medium">
                  {isDragging
                    ? 'Suelta tu archivo aquí'
                    : 'Arrastra tu archivo XLSX aquí'}
                </p>
                <p className="text-mid-gray text-sm mt-1">
                  o haz clic para seleccionar
                </p>
              </div>

              <p className="text-xs text-mid-gray">Formatos aceptados: .xlsx</p>
            </div>
          </div>
        </div>
      )}

      {status === 'parsing' && selectedFile && (
        <div className="w-full max-w-md">
          <div className="border border-light-gray rounded-lg p-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-primary border-t-transparent" />

              <div>
                <p className="text-near-black font-medium">
                  Procesando archivo...
                </p>
                <p className="text-sm text-mid-gray mt-1">
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="w-full max-w-md">
          <div className="border border-red-300 bg-red-50 rounded-lg p-6">
            <div className="flex flex-col items-center gap-4">
              <svg
                className="w-10 h-10 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>

              <div className="text-center">
                <p className="text-red-700 font-medium">Error al importar</p>
                <p className="text-sm text-red-600 mt-2 whitespace-pre-line">
                  {error}
                </p>
              </div>

              <button
                onClick={handleReset}
                className="mt-2 px-4 py-3 min-h-[44px] bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Intentar de nuevo
              </button>
            </div>
          </div>
        </div>
      )}

      {status === 'success' && summary && (
        <div className="w-full max-w-md">
          <div className="border border-green-300 bg-green-50 rounded-lg p-6">
            <div className="flex flex-col items-center gap-4">
              <svg
                className="w-10 h-10 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>

              <div className="text-center w-full">
                <p className="text-green-700 font-medium text-lg">
                  Importación exitosa
                </p>

                <div className="mt-4 space-y-2 text-sm text-green-800">
                  <p>
                    <span className="font-semibold">
                      {summary.transactionsImported}
                    </span>{' '}
                    transacciones importadas
                  </p>
                  {summary.duplicatesSkipped > 0 && (
                    <p className="text-green-600">
                      {summary.duplicatesSkipped} duplicados omitidos
                    </p>
                  )}
                  <p>
                    <span className="font-semibold">
                      {summary.cardsDetected}
                    </span>{' '}
                    {summary.cardsDetected === 1
                      ? 'tarjeta detectada'
                      : 'tarjetas detectadas'}
                  </p>
                  {summary.dateRange && (
                    <p>
                      {formatDateRange(summary.dateRange.start)} -{' '}
                      {formatDateRange(summary.dateRange.end)}
                    </p>
                  )}
                </div>

                {summary.errors.length > 0 && (
                  <div className="mt-4 p-3 bg-yellow-100 rounded text-left">
                    <p className="text-yellow-800 text-xs font-medium mb-1">
                      Advertencias:
                    </p>
                    <ul className="text-yellow-700 text-xs list-disc list-inside">
                      {summary.errors.slice(0, 3).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {summary.errors.length > 3 && (
                        <li>...y {summary.errors.length - 3} más</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-2">
                  <button
                    onClick={() => navigate('/transacciones')}
                    className="w-full px-4 py-3 min-h-[44px] bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors font-medium"
                  >
                    Ver transacciones
                  </button>
                  <button
                    onClick={handleReset}
                    className="w-full px-4 py-3 min-h-[44px] border border-brand-primary text-brand-primary rounded-lg hover:bg-brand-primary/5 transition-colors"
                  >
                    Importar otro archivo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
